import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { featuredProducts } from "@/mocks/products";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

const badgeColors: Record<string, string> = {
  "Top Seller": "bg-amber-400 text-white",
  "Hot": "bg-red-500 text-white",
  "Eco": "bg-green-500 text-white",
  "New": "bg-blue-500 text-white",
};

export default function ProductListingsPage() {
  useSEO({
    title: "Product Listings | Factory Direct Wholesale Products | Crown Global",
    description: "Browse factory-direct wholesale product listings on Crown Global. Request quotes from verified global suppliers. Electronics, apparel, machinery, auto parts, health & beauty, and more.",
    keywords: "product listings, wholesale products, factory direct, request quote, verified suppliers, bulk products",
    canonical: "/product-listings",
    ogTitle: "Product Listings — Crown Global Factory & Buyer",
    ogDescription: "Browse factory-direct wholesale products. Request quotes from verified global suppliers.",
    ogImage: "https://public.readdy.ai/ai/img_res/48346ec6c54015acab62a08cef00c0fd.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/product-listings`,
          "url": `${SITE_URL}/product-listings`,
          "name": "Factory Direct Product Listings — Crown Global",
          "description": "Browse factory-direct wholesale product listings from verified global suppliers.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Product Listings", "item": `${SITE_URL}/product-listings` }
            ]
          }
        },
        {
          "@type": "ItemList",
          "name": "Factory Direct Product Listings",
          "url": `${SITE_URL}/product-listings`,
          "numberOfItems": featuredProducts.length,
          "itemListElement": featuredProducts.slice(0, 8).map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Product",
              "name": p.name,
              "description": `Factory direct ${p.name} from ${p.vendor}, ${p.country}. Min order: ${p.minOrder} ${p.unit}.`,
              "brand": { "@type": "Brand", "name": p.vendor },
              "offers": {
                "@type": "Offer",
                "price": p.price,
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "url": `${SITE_URL}/product-listings`,
                "seller": { "@type": "Organization", "name": p.vendor }
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": p.rating,
                "reviewCount": p.reviews
              }
            }
          }))
        }
      ]
    }
  });

  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  const [qty, setQty] = useState(1);

  const product = selected !== null ? featuredProducts[selected] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900">Product Listings</h1>
            <p className="text-gray-500 text-sm mt-1">Browse and request quotes from verified global suppliers</p>
          </div>

          {!product ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
              {featuredProducts.map((p, i) => (
                <div key={p.id} onClick={() => { setSelected(i); setQty(p.minOrder); }} className="bg-white rounded-xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300">
                  <div className="relative w-full h-48 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    {p.badge && <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full ${badgeColors[p.badge] || ""}`}>{p.badge}</span>}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-gray-400 mb-1">{p.category}</p>
                    <h3 className="text-sm font-semibold text-gray-800 leading-snug mb-2 line-clamp-2">{p.name}</h3>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-amber-500 font-extrabold text-lg">${p.price.toLocaleString()}</span>
                      <span className="text-gray-400 text-xs">/ {p.unit}</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">Min. Order: {p.minOrder} {p.unit}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 truncate">{p.vendor}</span>
                      {p.verified && <span className="text-green-600 flex items-center gap-0.5 shrink-0"><i className="ri-shield-check-fill"></i> Verified</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-6 md:p-10">
              <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-amber-500 text-sm font-semibold mb-6 cursor-pointer hover:text-amber-600 transition-colors">
                <i className="ri-arrow-left-line"></i> Back to Listings
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="w-full h-80 rounded-xl overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  {product.badge && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badgeColors[product.badge] || ""}`}>{product.badge}</span>}
                  <h2 className="text-2xl font-extrabold text-gray-900 mt-3 mb-2">{product.name}</h2>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>)}
                    </div>
                    <span className="text-sm text-gray-500">({product.reviews} reviews)</span>
                    {product.verified && <span className="text-green-600 text-xs flex items-center gap-0.5 font-semibold"><i className="ri-shield-check-fill"></i> Verified Supplier</span>}
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-3xl font-extrabold text-amber-500">${product.price.toLocaleString()}</span>
                    <span className="text-gray-400 text-sm">/ {product.unit}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">Min. Order: {product.minOrder} {product.unit}</p>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-sm text-gray-600 font-medium">Quantity:</span>
                    <div className="flex items-center border border-gray-200 rounded-full overflow-hidden">
                      <button onClick={() => setQty(Math.max(product.minOrder, qty - 1))} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors">-</button>
                      <span className="px-4 text-sm font-semibold text-gray-800">{qty}</span>
                      <button onClick={() => setQty(qty + 1)} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors">+</button>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => navigate("/secure-payment")} className="flex-1 bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">Request Quote</button>
                    <button className="flex-1 border border-amber-400 text-amber-500 font-bold py-3 rounded-full hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap text-sm">Contact Supplier</button>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-sm text-gray-500"><strong className="text-gray-700">Supplier:</strong> {product.vendor} · {product.country}</p>
                    <p className="text-sm text-gray-500 mt-1"><strong className="text-gray-700">Category:</strong> {product.category}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
